/**
 * signature-ink.ts — the min-ink gate, as arithmetic.
 *
 * PURE MODULE: no canvas, no DOM, nothing but the coordinates `signature_pad` hands back.
 * `SignaturePad` mounts the pad and reports the answer; the rule that decides it lives here,
 * which is what lets a test exercise it without mounting anything.
 *
 * WHY there are two floors, and the defect the path floor was born from, is written at the top
 * of `SignaturePad.tsx` — where the gate is felt.
 */

/** Total CSS pixels the pen has to travel; below this is a tap, not a signature. */
export const MIN_PATH_PX = 120;
/** CSS pixels the ink has to span in one direction. */
export const MIN_SPAN_PX = 60;

/**
 * Both floors, over nothing but the geometry of the strokes. Staying pure is this module's whole
 * job: the gate is the part worth pinning in a test, and this repo's vitest runs without a DOM —
 * the rule has to be reachable without mounting a canvas.
 */
export function inkClearsFloors(strokes: { points: { x: number; y: number }[] }[]): boolean {
    let path = 0;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const stroke of strokes) {
        let previous: { x: number; y: number } | null = null;
        for (const point of stroke.points) {
            // Distance accumulates WITHIN a stroke only: the jump from one stroke's end to the
            // next one's start is a lifted pen, not ink.
            if (previous) path += Math.hypot(point.x - previous.x, point.y - previous.y);
            previous = point;

            if (point.x < minX) minX = point.x;
            if (point.x > maxX) maxX = point.x;
            if (point.y < minY) minY = point.y;
            if (point.y > maxY) maxY = point.y;
        }
    }

    if (path < MIN_PATH_PX) return false;
    return Math.max(maxX - minX, maxY - minY) >= MIN_SPAN_PX;
}
